import type { BuildVariant } from '@/store/useStudioStore';

type BuildTipDefinition = {
  icon: string;
  title: string;
  text: string;
  action?: string;
};

type BuildCopyDefinition = {
  badge: string;
  title: string;
  subtitle: string;
  steps: string[];
  note: string;
  note10min: string;
  note15min: string;
  expertName: string;
  expertSub: string;
  expertCopy: string;
  tipsTitle: string;
  tips: BuildTipDefinition[];
};

const standardCopy: BuildCopyDefinition = {
  badge: 'Build in progress',
  title: 'Your page is being created...',
  subtitle:
    'We are turning your brief into a conversion-focused first draft with structure, messaging, and CTA flow.',
  steps: [
    'Reading brief - understanding your product, audience, and the one action you want visitors to take',
    'Choosing page structure - deciding which sections to include and what order drives the clearest story',
    'Generating first draft - AI writes the initial copy pass for each section based on your brief',
    'Refining messaging - tightening headlines, sharpening CTAs, and removing friction from the narrative',
    'Laying out components - assembling section markup, responsive grid, and visual hierarchy',
    'Checking for gaps - verifying the page answers the top objections a visitor is likely to have',
    'Finalising and packaging - cleaning up the output and preparing it for Studio review',
  ],
  note: 'Most pages are ready in about 5 minutes, though more detailed requests can take longer. If the build goes past 10 minutes, we automatically refund 50% of the page credits. If it goes past 15 minutes, we refund 100%. We will email you as soon as the page is ready, so you can leave this page now without affecting the build.',
  note10min:
    'This build is taking longer than expected. We will automatically refund 50% of the page credits, and if it goes past 15 minutes, we will refund 100%. We will email you as soon as the page is ready, and closing this page will not interrupt the build.',
  note15min:
    'This build has been running for more than 15 minutes. We will automatically refund 100% of the page credits. We will email you as soon as the page is ready, and closing this page will not interrupt the build.',
  expertName: 'Your expert is building',
  expertSub: 'Auto-creating your page from the brief',
  expertCopy:
    'I am assembling your structure, copy, and CTA flow now. You can review and refine everything right after this step finishes.',
  tipsTitle: 'Before your draft opens',
  tips: [
    {
      icon: 'fa-solid fa-bullseye',
      title: 'Decide the first-screen test',
      text: 'When the draft opens, judge the hero by one question: would the right visitor know what you offer, why it matters, and what to do next?',
      action: 'First edit to try: ask for a sharper hero promise or a clearer primary CTA.',
    },
    {
      icon: 'fa-solid fa-layer-group',
      title: 'Scan the story order',
      text: 'Check whether the page moves from promise, to proof, to offer, to action. If a section feels early or late, ask Studio to reorder the flow.',
      action: 'Useful prompt: "Move the strongest proof closer to the first CTA."',
    },
    {
      icon: 'fa-solid fa-image',
      title: 'Prepare the missing assets',
      text: 'Have one logo, one product or service visual, and any customer proof ready. The first draft can be refined faster when those pieces are available.',
      action:
        'After review opens, select an image block and upload or replace the visual directly.',
    },
    {
      icon: 'fa-solid fa-comments',
      title: 'Write one concrete edit',
      text: 'Studio works best when the next instruction is specific. Mention the section, the desired change, and the reason behind it.',
      action:
        'Example: "Make the pricing section feel easier to compare for small business owners."',
    },
  ],
};

const cloneCopy: BuildCopyDefinition = {
  badge: 'Page recreation in progress',
  title: 'Your existing page is being recreated...',
  subtitle:
    'We are analyzing the source URL, rebuilding its structure, and turning it into an editable CoveAuto AI draft.',
  steps: [
    'Fetching source page - loading the URL and capturing the visible page structure',
    'Mapping sections - identifying hero, proof, offer, pricing, form, and footer patterns',
    'Extracting content signals - reading headlines, CTAs, images, and conversion cues',
    'Rebuilding layout - recreating the original flow as editable Studio sections',
    'Adapting assets - placing images, icons, and spacing into a responsive draft',
    'Checking fidelity - comparing key sections against the original page experience',
    'Preparing editable draft - packaging the recreated page for Studio review',
  ],
  note: 'Most page recreations are ready in about 5 minutes, though pages with complex layouts or protected assets can take longer. If the build goes past 10 minutes, we automatically refund 50% of the page credits. If it goes past 15 minutes, we refund 100%. We will email you as soon as the editable draft is ready, so you can leave this page now without affecting the build.',
  note10min:
    'This page recreation is taking longer than expected. We will automatically refund 50% of the page credits, and if it goes past 15 minutes, we will refund 100%. We will email you as soon as the editable draft is ready, and closing this page will not interrupt the build.',
  note15min:
    'This page recreation has been running for more than 15 minutes. We will automatically refund 100% of the page credits. We will email you as soon as the editable draft is ready, and closing this page will not interrupt the build.',
  expertName: 'Your expert is recreating the page',
  expertSub: 'Importing structure from the source URL',
  expertCopy:
    'I am mapping the original page sections, visual rhythm, and CTA path into an editable Studio draft. The source page stays unchanged.',
  tipsTitle: 'What happens during recreation',
  tips: [
    {
      icon: 'fa-solid fa-shield-halved',
      title: 'Original Page Stays Live',
      text: 'The source URL is only used as a reference. We do not publish changes, overwrite files, or affect the page visitors can already access.',
    },
    {
      icon: 'fa-solid fa-layer-group',
      title: 'Editable Structure',
      text: 'The recreated page becomes a Studio draft with editable sections, text, images, and CTAs instead of a flat screenshot.',
    },
    {
      icon: 'fa-solid fa-wand-magic-sparkles',
      title: 'Cleaner Draft',
      text: 'We keep the recognizable layout direction while cleaning spacing, responsive behavior, and conversion flow where the original page needs help.',
    },
    {
      icon: 'fa-solid fa-image',
      title: 'Asset Matching',
      text: 'When an original asset cannot be reused directly, the draft keeps the same intent and leaves the replacement easy to swap in Studio.',
    },
    {
      icon: 'fa-solid fa-code-compare',
      title: 'Review The Differences',
      text: 'After recreation, review the draft section by section. Your expert will call out where the editable version intentionally differs from the source.',
    },
  ],
};

const productCopy: BuildCopyDefinition = {
  badge: 'Product page build in progress',
  title: 'Your product sales page is being created...',
  subtitle:
    'We are turning the selected products, commerce links, proof, and offer direction into a paid-traffic-ready landing page.',
  steps: [
    'Reading product analysis - checking selected products, URLs, prices, images, and proof signals',
    'Choosing selling angle - matching the hero offer to the campaign objective',
    'Structuring commerce flow - placing product details, proof, FAQ, and repeated buy CTAs',
    'Preserving purchase links - making sure CTAs point to the original product pages',
    'Generating page draft - assembling responsive HTML, CSS, tracking attributes, and content',
    'Checking claim safety - removing unsupported reviews, discounts, scarcity, and guarantees',
    'Preparing editable draft - packaging the product page for Studio review',
  ],
  note: 'Most product pages are ready in about 5 minutes, though product-heavy pages can take longer. If the build goes past 10 minutes, we automatically refund 50% of the page credits. If it goes past 15 minutes, we refund 100%. We will email you as soon as the page is ready, so you can leave this page now without affecting the build.',
  note10min:
    'This product page build is taking longer than expected. We will automatically refund 50% of the page credits, and if it goes past 15 minutes, we will refund 100%. We will email you as soon as the draft is ready, and closing this page will not interrupt the build.',
  note15min:
    'This product page build has been running for more than 15 minutes. We will automatically refund 100% of the page credits. We will email you as soon as the draft is ready, and closing this page will not interrupt the build.',
  expertName: 'Your expert is building',
  expertSub: 'Creating a product sales page',
  expertCopy:
    'I am using the selected products and original purchase links to build a focused sales page. You can review the copy, sections, and CTAs when it finishes.',
  tipsTitle: 'Product page checks',
  tips: [
    {
      icon: 'fa-solid fa-bag-shopping',
      title: 'Selected Products',
      text: 'Only the products you selected are featured, so the page stays focused on the campaign offer.',
    },
    {
      icon: 'fa-solid fa-mouse-pointer',
      title: 'Purchase Links',
      text: 'Buy CTAs are generated from the original product URLs captured during analysis.',
    },
    {
      icon: 'fa-solid fa-badge-check',
      title: 'Commerce Proof',
      text: 'Reviews, prices, specs, and guarantees are used only when they were present in the provided product data.',
    },
    {
      icon: 'fa-solid fa-layer-group',
      title: 'Conversion Flow',
      text: 'The page is organized around hero offer, product details, proof, FAQ, and final purchase action.',
    },
    {
      icon: 'fa-solid fa-pen',
      title: 'Editable Draft',
      text: 'After generation, you can edit sections, replace images, and refine product copy directly in Studio.',
    },
  ],
};

export const getStudioBuildCopy = (variant: BuildVariant): BuildCopyDefinition =>
  variant === 'clone' ? cloneCopy : variant === 'product' ? productCopy : standardCopy;
